import LogisticsCompany from "@/enum/LogisticsCompany";
import { isHistoryOrder, isMergeOrder } from "@/utils/OrderUtils";
import { get, post } from "../http/platformServer";
import OrderMergeSplitStatus from "../../enum/OrderMergeSplitStatus";
import EwaybillDTO from "@/dto/order/EwaybillDTO";
import OrderDTO from "@/dto/order/OrderDTO";
import { getEwaybillAccountList, loadEwaybillAccountsList } from "./setting/ewaybillAccount";
import ShippingAddress from "@/dto/ewaybillAccount/ShippingAddressDTO";
import BranchAccountDTO from "@/dto/ewaybillAccount/BranchAccountDTO";
import SubscriptionDTO from "@/dto/ewaybillAccount/SubscriptionDTO";
import EwaybillAccountDTO from "@/dto/ewaybillAccount/EwaybillAccountDTO";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import ShippingAddressDTO from "@/dto/ShippingAddressDTO";
import { Modal } from "antd";
import EwaybillType from "@/enum/EwaybillType";
import mainServerClient from "../http/mainServerClient";
import { PagedResult } from "./common.types";
import WaybillTemplatePrintType from "@/enum/WaybillTemplatePrintType";
import { PremiumFunctionType } from "../PremiumValidator";
import OrderItemDTO from "@/dto/order/OrderItemDTO";
import { GetPrintDataParams } from "../ReprintEwaybill/type";
import { EwaybillChildOrderDto } from "./ewaybillGenerate";
import { ExtendAxiosRequestConfig } from "../http/types";
import { retry } from "@/utils/retry";

// 简化订单商品信息
const simplifyOrderItems = (orderItems: any) => {
    if (!orderItems || orderItems.length === 0) {
        return [];
    }
    return orderItems.map((orderItem: any) => {
        const leanOrderItem = {
            id: orderItem.id,
            title: orderItem.simpleTitle || orderItem.title,
            originalId: orderItem.originalId,
            quantity: orderItem.quantity,
        };

        if (orderItem.localSkuProperties || orderItem.skuProperties) {
            (leanOrderItem as any).skuProperties = orderItem.localSkuProperties || orderItem.skuProperties;
        }

        if (orderItem.weight) {
            (leanOrderItem as any).weight = orderItem.weight;
        }

        // 合并订单需要将OrderItem对应的order信息回传
        if (orderItem.order) {
            (leanOrderItem as any).order = orderItem.order;
        }
        return leanOrderItem;
    });
};

// 简化订单信息，只需要获取电子面单所需订单信息
const simplifyOrder = (order: OrderDTO, selectedOrderItems: OrderItemDTO[]) => {
    const { id, parentId, shopId, originalId, status, mergeSplitStatus, isRefunding } = order;

    const childOrderCount = order.childOrders ? order.childOrders.length : null;

    const orderDto: any = { id, parentId, shopId, originalId, status, mergeSplitStatus, isRefunding, childOrderCount };
    const selectedOrderItemsLength = selectedOrderItems ? selectedOrderItems.length : 0;
    const orderItemsLength = order.orderItems ? order.orderItems.length : 0;

    orderDto.isAllOrderItemsSelected = selectedOrderItemsLength === orderItemsLength; // 是否全选OrderItem
    orderDto.orderItems = simplifyOrderItems(selectedOrderItems);

    if (isMergeOrder(order)) {
        orderDto.childOrders = order.childOrders.map<EwaybillChildOrderDto>((childOrder) => {
            const { originalId, id, orderItems, isSelected } = childOrder;

            return {
                status,
                /** 无勾选状态默认当作选中 */
                isPrintingEwaybill: isSelected,
                originalId,
                id,
                isWithoutOrderItems: !orderItems || orderItems?.length === 0,
            };
        });
    }

    return orderDto;
};

/**
 * 获取合并订单选中的orderItems
 * 用户可能勾选某个或者某几个childOrder打印，因此获取被选中orderItems时，orderItem中要封装order转换后台方便后台判断勾选了哪些被合并订单
 * @param order
 * @return {Array}
 */
const getSelectedOrderItemsOfMergeParentOrder = (order: any) => {
    const orderItemIdAndOrderMap = {}; // {orderItemId: simpleOrderObject}

    order.childOrders.forEach((childOrder: any) => {
        childOrder.orderItems.forEach((orderItem: any) => {
            // 复制部分order信息，是为了打印时将orderItem对应的order信息传回后台

            orderItemIdAndOrderMap[orderItem.id] = {
                id: childOrder.id,
                originalId: childOrder.originalId,
                shopId: childOrder.shopId,
            };
        });
    });

    const selectedOrderItems: any = [];

    order.orderItems.forEach((orderItem: any) => {
        if (orderItem.isSelected) {
            orderItem.order = orderItemIdAndOrderMap[orderItem.id];
            selectedOrderItems.push(orderItem);
        }
    });

    return selectedOrderItems;
};

/**
 * 简化order数据，只把需要的数据传给后台,提升网络传送性能
 */
const buildOrderDto = (order: OrderDTO) => {
    let selectedOrderItems;

    if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT) {
        selectedOrderItems = getSelectedOrderItemsOfMergeParentOrder(order);
    } else {
        // @ts-ignore
        selectedOrderItems = order.orderItems.filter((orderItem) => orderItem.isSelected);
    }

    return simplifyOrder(order, selectedOrderItems);
};

const buildWaybillTemplateDto = (waybillTemplate: WaybillTemplateDTO) => ({
    id: waybillTemplate.id,
    name: waybillTemplate.name,
    logisticsCompany: waybillTemplate.logisticsCompany,
    printTemplateId: waybillTemplate.printTemplateId,
    serviceOptions: waybillTemplate.serviceOptions,
});

export const buildEwaybillAccountDto = (
    ewaybillShippingAddressId: number,
    ewaybillAccountMapping: Map<number, EwaybillAccountDTO>,
) => {
    const ewaybillAccount = ewaybillAccountMapping.get(ewaybillShippingAddressId);

    if (ewaybillAccount) {
        return {
            id: ewaybillAccount.id,
            shopId: ewaybillAccount.shopId,
            type: ewaybillAccount.type,
            validationToken: ewaybillAccount.validationToken,
            platformUsername: ewaybillAccount.platformUsername,
        };
    }

    return {};
};

export const buildEwaybillShippingAddressDto = (
    ewaybillShippingAddressId: number,
    ewaybillShippingAddressMapping: Map<number, ShippingAddress>,
) => {
    const ewaybillShippingAddress = ewaybillShippingAddressMapping.get(ewaybillShippingAddressId);

    if (!ewaybillShippingAddress) {
        return {};
    }

    return {
        id: ewaybillShippingAddress.id,
        province: ewaybillShippingAddress.province,
        city: ewaybillShippingAddress.city,
        district: ewaybillShippingAddress.district,
        town: ewaybillShippingAddress.town,
        street: ewaybillShippingAddress.street,
    };
};

export interface EwaybillGenerateResult {
    errors: string[];

    ewaybills: EwaybillDTO[];

    orderId: number;
}

export async function loadEwaybillAccountInfo() {
    const ewaybillAccount = await getEwaybillAccountList();

    const ewaybillShippingAddressMapping = new Map<number, ShippingAddress>();
    const branchAccountMapping = new Map<number, BranchAccountDTO>();
    const subscriptionMapping = new Map<number, SubscriptionDTO>();
    const ewaybillAccountMapping = new Map<number, EwaybillAccountDTO>();
    const ewaybillAccountId2AccountMapping = new Map<number, EwaybillAccountDTO>();

    ewaybillAccount.forEach((account) => {
        ewaybillAccountId2AccountMapping.set(account.id, account);
        account.subscriptions.forEach((subscription) => {
            subscription.branchAccounts.forEach((branchAccount) => {
                branchAccount.shippingAddresses.forEach((shippingAddress) => {
                    ewaybillShippingAddressMapping.set(shippingAddress.id, shippingAddress);

                    branchAccountMapping.set(shippingAddress.id, branchAccount);

                    subscriptionMapping.set(shippingAddress.id, subscription);

                    ewaybillAccountMapping.set(shippingAddress.id, account);
                });
            });
        });
    });

    return {
        ewaybillAccountMapping,
        subscriptionMapping,
        branchAccountMapping,
        ewaybillShippingAddressMapping,
        ewaybillAccountId2AccountMapping,
    };
}

export interface OldWaybillPrintDataParams {
    shopId: number;

    waybillTemplate: WaybillTemplateDTO;

    ewaybill: EwaybillDTO;
}

interface RealOldWaybillPrintDataParams {
    shopId: number;

    ewaybillId: number;

    /** 抖店使用 */
    ewaybillAccount?: {
        id: number;

        shopId: number;

        type: EwaybillType;

        platformUsername: string;

        ewaybillShippingAddressIds: number[];
    };

    /** 打印类型为顺丰模板 */
    isSfPrintType?: boolean;
}

const JinRiTeMaiEwaybillTypes = [
    EwaybillType.JINRITEMAI,
    EwaybillType.JINRITEMAI_SHARE,
    EwaybillType.JINRITEMAI_SHOP_AUTH,
    EwaybillType.JINRITEMAI_IOP,
    EwaybillType.JINRITEMAI_AUTH,
    EwaybillType.JINRITEMAI_AUTH_SHARE,
    EwaybillType.JINRITEMAI_SUPPLIER,
];

// 获取旧电子面单PrintData
export const getOldWaybillPrintData = async (
    params: OldWaybillPrintDataParams,
): Promise<{ id: number; content: string }> => {
    const { shopId, ewaybill, waybillTemplate } = params;

    const ewaybillShippingAddressIds = [];

    const realParams: RealOldWaybillPrintDataParams = {
        shopId,
        ewaybillId: ewaybill.id,
    };

    let ewaybillAccount: EwaybillAccountDTO;

    let shouldThrowError = false;

    if (JinRiTeMaiEwaybillTypes.includes(ewaybill.type)) {
        const { ewaybillAccountMapping } = await loadEwaybillAccountsList(
            JinRiTeMaiEwaybillTypes,
            waybillTemplate.logisticsCompany,
        );

        ewaybillAccount = ewaybillAccountMapping.get(waybillTemplate.ewaybillShippingAddressId);

        if (!ewaybillAccount) {
            shouldThrowError = true;
        }
    } else if (waybillTemplate.printType === WaybillTemplatePrintType.SF) {
        const { ewaybillAccountMapping } = await loadEwaybillAccountsList(
            [EwaybillType.OFFICIAL, EwaybillType.OFFICIAL_SHARE],
            waybillTemplate.logisticsCompany,
        );

        ewaybillAccount = ewaybillAccountMapping.get(waybillTemplate.ewaybillShippingAddressId);

        if (!ewaybillAccount) {
            shouldThrowError = true;
        }
    } else if (
        [EwaybillType.OFFICIAL, EwaybillType.OFFICIAL_SHARE].includes(ewaybill.type) &&
        waybillTemplate.logisticsCompany === LogisticsCompany.JD_HPCP
    ) {
        const { ewaybillAccountMapping } = await loadEwaybillAccountsList(
            [EwaybillType.OFFICIAL, EwaybillType.OFFICIAL_SHARE],
            waybillTemplate.logisticsCompany,
        );

        ewaybillAccount = ewaybillAccountMapping.get(waybillTemplate.ewaybillShippingAddressId);

        if (!ewaybillAccount) {
            shouldThrowError = true;
        }
    }

    if (shouldThrowError) {
        throw new Error("该快递单模板对应的电子面单账号已被删除，请重新选择快递单模板");
    }

    if (ewaybillAccount) {
        ewaybillAccount.subscriptions.forEach((subscription) => {
            subscription.branchAccounts.forEach((branchAccount) => {
                branchAccount.shippingAddresses.forEach((shippingAddress) => {
                    ewaybillShippingAddressIds.push(shippingAddress.id);
                });
            });
        });

        realParams.ewaybillAccount = {
            id: ewaybillAccount.id,
            shopId: ewaybillAccount.shopId,
            type: ewaybillAccount.type,
            platformUsername: ewaybillAccount.platformUsername,
            ewaybillShippingAddressIds,
        };
    }

    if (waybillTemplate.printType === WaybillTemplatePrintType.SF) {
        realParams.isSfPrintType = true;
    }

    return post(params.shopId, "/ewaybill/print_data", realParams);
};

interface PrintDataResult {
    id: number;
    content: string;
}

export function getPrintData(params: GetPrintDataParams, config?): Promise<PrintDataResult> {
    return post(params.shopId, "/ewaybill/print_data", params, config);
}

/**
 * 获取已生成的电子面单（单个订单）
 * @param needDetails 是否需要加载电子面单详情（打印时需要）
 *
 */
export const load = (order: any, needDetails: any): Promise<EwaybillDTO[]> => {
    const shopId = order.shopId;
    const params = {
        shopId,
        orderId: order.id,
        orderMergeSplitStatus: order.mergeSplitStatus,
        needDetails,
    };

    return post(shopId, "/ewaybill/load", params);
};
/**
 * 批量获取旧电子面单（多个订单）
 *
 */

export type EwaybillMap = {
    [orderId: number]: EwaybillDTO[];
};

export const batchLoad = (orders: OrderDTO[], needDetails: boolean): Promise<EwaybillMap> => {
    const shopId = orders[0].shopId;

    const data = {
        shopId,
        needDetails,
        orders: orders.map((it) => ({
            id: it.id,
            status: it.status,
            mergeSplitStatus: it.mergeSplitStatus,
        })),
    };

    return post(shopId, "/ewaybill/batch_load", data);
};

interface CancelEwaybillParams {
    shopId: number;
    orderId: number;
    isHistoryOrder: boolean;
    ewaybills: EwaybillParams[];
    originalIds: string[];
}

async function buildCancelEwaybillParams(
    order: OrderDTO,
    ewaybills: EwaybillDTO[],
    ewaybillAccountMapping: Map<number, EwaybillAccountDTO>,
    ewaybillAccountId2AccountMapping: Map<number, EwaybillAccountDTO>,
    showErrorModal: boolean = true,
) {
    const ewaybillsParams: EwaybillParams[] = [];

    const deletedEwaybillShippingAddressInfoMap = new Map<
        number,
        {
            ewaybillAccountId: number;
            ewaybillShippingAddressId: number;
        }
    >();

    const softDeletedEwaybillAccountMap = new Map<number, EwaybillAccountDTO>();

    for (const ewaybill of ewaybills) {
        const ewaybillShippingAddressId = ewaybill?.details?.ewaybillShippingAddressId;
        let ewaybillAccount = ewaybillAccountMapping.get(ewaybillShippingAddressId);

        if (!ewaybillAccount && ewaybillShippingAddressId) {
            /** 可能是地址修改了，从接口获取最新的发货地址id */

            const res = deletedEwaybillShippingAddressInfoMap.has(ewaybillShippingAddressId)
                ? deletedEwaybillShippingAddressInfoMap.get(ewaybillShippingAddressId)
                : await getDeletedEwaybillShippingAddressInfo(ewaybillShippingAddressId);

            if (res) {
                const { ewaybillAccountId } = res;
                deletedEwaybillShippingAddressInfoMap.set(ewaybillAccountId, res);
                ewaybillAccount = ewaybillAccountId2AccountMapping.get(ewaybillAccountId);
            }
        }

        if (!ewaybillAccount && ewaybillShippingAddressId) {
            /** 可能软删除，从接口获取软删除电子面单账号 */
            const res = softDeletedEwaybillAccountMap.has(ewaybillShippingAddressId)
                ? [softDeletedEwaybillAccountMap.get(ewaybillShippingAddressId)]
                : await getSoftDeletedEwaybillAccount([ewaybillShippingAddressId]);

            if (res?.[0]) {
                softDeletedEwaybillAccountMap.set(ewaybillShippingAddressId, res[0]);
                ewaybillAccount = res[0];
            }
        }

        if (!ewaybillAccount) {
            if (showErrorModal) {
                Modal.error({
                    title: "构建回收电子面单参数失败",
                    content: "无法找到电子面单账号",
                });
            }

            throw new Error("无法找到电子面单账号");
        }

        const ewaybillParams = {
            id: ewaybill.id,
            waybillNo: ewaybill.waybillNo,
            logisticsCompany: ewaybill.logisticsCompany,
            parentWaybillNo: ewaybill?.parentWaybillNo,
            ewaybillAccount: {
                id: ewaybillAccount.id,
                shopId: ewaybillAccount.shopId,
                type: ewaybillAccount.type,
            },
        };

        ewaybillsParams.push(ewaybillParams);
    }

    const originalIds = [];

    if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
        order.childOrders.forEach(({ originalId }) => {
            originalIds.push(originalId);
        });
    } else {
        originalIds.push(order.originalId);
    }

    const params: CancelEwaybillParams = {
        shopId: order.shopId,
        orderId: order.id,
        isHistoryOrder: isHistoryOrder(order),
        ewaybills: ewaybillsParams,
        originalIds,
    };

    return params;
}

export interface EwayBillCancelReturn {
    canceledEwaybillIds: number[];
    errorMap: {
        [ewaybillId: number]: string;
    };
    newHasEwaybillNo: boolean;
    newLogisticsCompany: LogisticsCompany;
    newWaybillNo: string;
    orderId: number;
}

interface EwaybillAccountParams {
    id: number;

    shopId: number;

    type: string;
}

export interface EwaybillParams {
    id: number;

    waybillNo: string;

    logisticsCompany: LogisticsCompany;

    ewaybillAccount: EwaybillAccountParams;
}

/**
 * 回收电子面单号
 */
export const cancel = async (order: OrderDTO, ewaybills: EwaybillDTO[]): Promise<EwayBillCancelReturn> => {
    const shopId = order.shopId;

    const { ewaybillAccountMapping, ewaybillAccountId2AccountMapping } = await loadEwaybillAccountInfo();

    const params = await buildCancelEwaybillParams(
        order,
        ewaybills,
        ewaybillAccountMapping,
        ewaybillAccountId2AccountMapping,
    );

    return post(shopId, "/ewaybill/cancel", params);
};

/**
 *
 * 批量回收电子面单号轮训调用
 */
export async function batchCancel(
    ewaybillAccountMapping: Map<number, EwaybillAccountDTO>,
    ewaybillAccountId2AccountMap: Map<number, EwaybillAccountDTO>,
    order: OrderDTO,
    ewaybills: EwaybillDTO[],
    showErrorModal: boolean = true,
): Promise<EwayBillCancelReturn> {
    const params = await buildCancelEwaybillParams(
        order,
        ewaybills,
        ewaybillAccountMapping,
        ewaybillAccountId2AccountMap,
        showErrorModal,
    );

    const shopId = order.shopId;

    return await post(shopId, "/ewaybill/cancel", params);
}

const buildUpdateEwaybill = (shopId: number, ewaybill: EwaybillDTO) => {
    return {
        shopId,
        id: ewaybill.id,
        packageId: ewaybill.details.packageId,
        type: ewaybill.type,
        logisticsCompany: ewaybill.logisticsCompany,
        waybillNo: ewaybill.waybillNo,
        parentWaybillNo: ewaybill.parentWaybillNo,
        printData: ewaybill.printData,
    };
};

/*
 * 构建提交到服务器的电子面单数据，用于更新
 */
const buildUpdateEwaybillNoDtos = (shopId: number, ewaybills: EwaybillDTO[]) => {
    return ewaybills.map((ewaybill) => buildUpdateEwaybill(shopId, ewaybill));
};

export enum UpdateEwaybillOperationType {
    REPRINT = "REPRINT",
    PREVIEW = "PREVIEW",
}

/**
 * 更新一个订单的多个电子面单账号，当电子面单和订单收货人信息不一致是，调用接口更新电子面单的信息（收货人...）
 * @param order 订单
 * @param ewaybills 需要更新的电子面单
 * @param waybillTemplate 快递单模板
 * @param shippingAddress 发货易中的发货地址
 */
export const update = async (
    order: OrderDTO,
    ewaybills: EwaybillDTO[],
    waybillTemplate: WaybillTemplateDTO,
    shippingAddress: ShippingAddressDTO,
    operationType: UpdateEwaybillOperationType = UpdateEwaybillOperationType.REPRINT,
) => {
    let ewaybillAccount;

    if (waybillTemplate.ewaybillShippingAddressId) {
        const infoMapping = await loadEwaybillAccountInfo();

        ewaybillAccount = buildEwaybillAccountDto(
            waybillTemplate.ewaybillShippingAddressId,
            infoMapping.ewaybillAccountMapping,
        );
    }

    for (let i = 0; i < ewaybills.length; i++) {
        const ewaybill = ewaybills[i];

        await updateOne(order, ewaybill, waybillTemplate, shippingAddress, ewaybillAccount, operationType);
    }

    return true;
};

export const updateOne = async (
    order: OrderDTO,
    ewaybill: EwaybillDTO,
    waybillTemplate: WaybillTemplateDTO,
    shippingAddress: ShippingAddressDTO,
    ewaybillAccount: EwaybillAccountDTO,
    operationType: UpdateEwaybillOperationType = UpdateEwaybillOperationType.REPRINT,
) => {
    const params = {
        order: buildOrderDto(order),
        isHistoryOrder: isHistoryOrder(order),
        ewaybill: buildUpdateEwaybill(order.shopId, ewaybill),
        waybillTemplate: buildWaybillTemplateDto(waybillTemplate),
        shippingAddress,
        ewaybillAccount,
        operationType,
    };

    return post(order.shopId, "/ewaybill/update_one", params);
};

const ewaybillApi = {
    cancel,
    update,
    load,
    batchLoad,
};

export interface WaybillNoCancelResults {
    errorMsg: string;

    success: boolean;

    waybillNo: string;
}

interface ManualCancelEwaybillResult {
    errorCount: number;

    successCount: number;

    waybillNoCancelResults: WaybillNoCancelResults[];
}

interface ManualCancelEwaybillParams {
    ewaybillAccountId: number;

    logisticsCompany: LogisticsCompany;

    waybillNoes: string[];
}

export function manualCancelEwaybill(params: ManualCancelEwaybillParams): Promise<ManualCancelEwaybillResult> {
    return mainServerClient.post("/ewaybill/manuallyCancel", params);
}

export interface QueryCanceledEwaybillParams {
    shopId: number;
    canceledEnd: string;
    canceledStart: string;
    ewaybillShippingAddressIds?: number[];
    orderOriginalId?: string;
    waybillNo?: string;
    pageNo: number;
    pageSize: number;
}

export interface QueryCanceledEwaybillResult {
    branchName: string;
    canceledTime: string;
    opUsername: string;
    orderOriginalIds: string;
    shopName: string;
    waybillNo: string;
    ewaybillShippingAddressId: number;
}

/**
 * 查询运单号回收记录
 */
export function queryCanceledEwaybill(params: QueryCanceledEwaybillParams) {
    return post<PagedResult<QueryCanceledEwaybillResult>>(params.shopId, "/ewaybill/query_canceled", params);
}

export interface CreateCanceledEwaybillExportTaskParams {
    shopIds?: number[];
    waybillNo?: string;
    canceledStart?: string;
    canceledEnd?: string;
}

export interface MultiShopCanceledEwaybillExportTask extends CreateCanceledEwaybillExportTaskParams {
    dateCreated: string;
    lastUpdated: string;
    id: number;
    distributedTaskId: number;
    companyId: number;
    opUserId: number;
    opUserName: string;
    opShopId: number;
    status: "PENDING" | "RUNNING" | "COMPLETED" | "ERROR";
    recordCount: number;
    error: string;
    completedTime: string;
}

/** 提交当前查询条件对应的多店铺运单号回收记录导出任务 */
export function createCanceledEwaybillExportTask(params: CreateCanceledEwaybillExportTaskParams) {
    return mainServerClient.post<MultiShopCanceledEwaybillExportTask>("/canceled_ewaybill_export_task", params);
}

/** 查询指定多店铺运单号回收记录导出任务的状态 */
export function getCanceledEwaybillExportTask(taskId: number) {
    return mainServerClient.get<MultiShopCanceledEwaybillExportTask>(`/canceled_ewaybill_export_task/${taskId}`);
}

/** 获取已完成的多店铺运单号回收记录导出文件下载链接 */
export function getCanceledEwaybillExportTaskDownloadUrl(taskId: number) {
    return mainServerClient.get<string>("/canceled_ewaybill_export_task/download_url", { taskId });
}

/**
 * 导出运单号回收记录，返回下载链接
 */
export function submitExportCanceledEwaybillTask(params: Omit<QueryCanceledEwaybillParams, "pageNo" | "pageSize">) {
    return post<CanceledEwaybillExportTask>(params.shopId, "/canceled_ewaybill_export_task/submit", params);
}

interface QueryExportCanceledEwaybillTaskParams {
    pageNo: number;

    pageSize: number;

    shopIds: number[];
}

export interface CanceledEwaybillExportTask {
    id: number;
    shopId: number;
    created: string;
    errorMsg: string;
    status: "COMPLETED" | "ERROR" | "PENDING" | "RUNNING";
}

export function queryExportCanceledEwaybillTask(params: QueryExportCanceledEwaybillTaskParams) {
    return post<PagedResult<CanceledEwaybillExportTask>>(
        params.shopIds[0],
        "/canceled_ewaybill_export_task/query",
        params,
    );
}

export function getExportCanceledEwaybillTaskDownloadUrl(shopId: number, taskId: number) {
    return get<string>(shopId, "/canceled_ewaybill_export_task/download", {
        taskId,
    });
}

interface EwaybillShippingAddressDailyStatsTask {
    accountAddressId: number;
    allocatedQuantity: number;
    canceledQuantity: number;
    id: number;
}

export interface EwaybillBranchAccountDailyStatsTask {
    id: number;
    shopId: number;
    addressStatsTasks: EwaybillShippingAddressDailyStatsTask[];
    branchAccountId: number;
    branchName: string;
    created: string;
    errorMsg: string;
    status: "COMPLETED" | "ERROR" | "RUNNING";
}

interface QueryWaybillStatsParams {
    /** 按天使用 */
    queryTime?: string;

    /** 按月使用 */
    queryStartTime?: string;
    queryEndTime?: string;
}

export function queryWaybillStats(params: QueryWaybillStatsParams, config?: ExtendAxiosRequestConfig) {
    return mainServerClient.post<EwaybillBranchAccountDailyStatsTask[]>("/stats/waybill/list", params, config);
}

export interface WaybillStatsOverview {
    /**
     * 是否有店铺开启了发货统计
     */
    hasSettingEnableShop: boolean;

    /**
     * 昨天的取号数总数
     */
    yesterdayAllocatedQuantity: number;

    /**
     * 昨天的回收总数
     */
    yesterdayCanceledQuantity: number;
}

export function getWaybillStatsOverview(): Promise<WaybillStatsOverview> {
    return mainServerClient.get("/stats/waybill/overview", undefined, { hideModal: true });
}

export interface WaybillStatsSetting {
    branchAccountIds: number[];
    dateCreated: string;
    enabled: boolean;
    lastUpdated: string;
    shopId: number;
}

export function saveWaybillStatsSetting(
    statsSetting: Pick<WaybillStatsSetting, "branchAccountIds" | "shopId" | "enabled">[],
) {
    return mainServerClient.post("/stats/waybill/update_setting", statsSetting, {
        pathPremiumValidator() {
            return {
                type: PremiumFunctionType.WAYBILL_STATS,
            };
        },
    });
}

export function getWaybillStatsSetting() {
    return mainServerClient.get<WaybillStatsSetting[]>("/stats/waybill/setting");
}

export interface EwayBillDeleteReturn {
    deletedEwaybillIds: number[];
    errorMap: {
        [additionalPropName: string]: string;
    };
    newHasEwaybillNo: boolean;
    newLogisticsCompany: LogisticsCompany;
    newWaybillNo: string;
    orderId: number;
}

export async function batchDeleted(values: [OrderDTO, EwaybillDTO[]][]): Promise<{ items: EwayBillDeleteReturn[] }> {
    const { ewaybillAccountMapping, ewaybillAccountId2AccountMapping } = await loadEwaybillAccountInfo();

    const [order] = values[0];

    const shopId = order.shopId;

    const params = { orders: [], shopId };

    for (const [order, ewaybills] of values) {
        const p = await buildCancelEwaybillParams(
            order,
            ewaybills,
            ewaybillAccountMapping,
            ewaybillAccountId2AccountMapping,
        );

        params.orders.push(p);
    }

    return post(shopId, "/ewaybill/delete_relations", params);
}

interface Waybill {
    logisticsCompany: LogisticsCompany;
    parentWaybillNo: string;
    waybillNo: string;
    orderId: number;
    originalIds: string[];
}

export interface PddFdsWaybillCancelParams {
    shopId: number;
    waybills: Waybill[];
}

/** 拼多多取消回传运单号 */
export async function pddFdsEwaybillCancel(orders: OrderDTO[]) {
    const params: PddFdsWaybillCancelParams = { shopId: null, waybills: [] };

    const ewaybillMap = await batchLoad(orders, true);

    for (const order of orders) {
        params.shopId = order.shopId;

        if (!order.waybillNo) {
            throw new Error("订单的运单号不存在");
        }

        if (!order.logisticsCompany) {
            throw new Error("订单的快递公司不存在");
        }

        const ewaybills = ewaybillMap[order.id] || [];

        let parentWaybillNo = null;

        const ewaybill = ewaybills.find((it) => it.waybillNo === order.waybillNo);

        if (ewaybill) {
            parentWaybillNo = ewaybill.parentWaybillNo;
        }

        const originalIds = [];

        if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
            order.childOrders.forEach(({ originalId }) => {
                originalIds.push(originalId);
            });
        } else {
            originalIds.push(order.originalId);
        }

        const waybill: Waybill = {
            logisticsCompany: order.logisticsCompany,
            parentWaybillNo,
            waybillNo: order.waybillNo,
            orderId: order.id,
            originalIds,
        };

        params.waybills.push(waybill);
    }

    await post(params.shopId, "/pdd_fds/ewaybill_cancel", params);
}

/** 查询已删除的电子面单发货地址信息 */
export function getDeletedEwaybillShippingAddressInfo(
    ewaybillShippingAddressId: number,
): Promise<{ ewaybillAccountId: number; ewaybillShippingAddressId: number }> {
    return mainServerClient.post("/deletedEwaybillShippingAddressInfo", { ewaybillShippingAddressId });
}

/** 查询软删除的电子面单账号 */
export function getSoftDeletedEwaybillAccount(ewaybillShippingAddressIds: number[]): Promise<EwaybillAccountDTO[]> {
    return mainServerClient.post("/deleted_ewaybill_account", { ewaybillShippingAddressIds });
}

interface OnlineShipOpenStatusResult {
    shopId: number;
    hasOpen: boolean;
}

/**
 * 批量检测抖店在线寄件开启状态
 */
export function batchCheckDoudianOnlineShipOpenStatus(shopIds: number[]) {
    return post<OnlineShipOpenStatusResult[]>(shopIds[0], "/ewaybill/batch_check_jinritemai_online_ship_open_status", {
        shopIds,
    });
}

export interface GetDoudianOnlineShipExpressParams {
    shopId: number;
    shippingAddress: Pick<ShippingAddressDTO, "province" | "city" | "district" | "street">;
    orders: {
        originalId: string;
        /**
         * 保价费用
         */
        insure: number;
        /**
         * 重量 单位克
         */
        weight: number;
    }[];
}

export interface DoudianOnlineShipExpressInfo {
    logisticsCompany: LogisticsCompany;
    /**
     * 总重量
     */
    totalWeight: number;
    /**
     * 预计总费用
     */
    totalCost: number;
    /**
     * 邮费
     */
    totalPostage: number;
    /**
     * 总保价费用
     */
    totalInsure: number;
    /**
     * 截单时间
     */
    cutOffTime: string;
    /**
     * 推荐理由
     */
    recommendedReason: string;
}

export function getDoudianOnlineShipExpressInfoList(params: GetDoudianOnlineShipExpressParams) {
    return post<DoudianOnlineShipExpressInfo[]>(params.shopId, "/ewaybill/get_online_ship_express_info_list", params);
}

export interface PrintIndexInfo {
    /**
     * 电子面单号ID
     */
    ewaybillId?: number | null;
    /**
     * 打印序号，格式：1/25
     */
    printIndex?: null | string;
}

export interface ShopPrintIndexInfo {
    shopId: number;

    printIndexInfos: PrintIndexInfo[];
}

/** 更新电子面单打印序号 */
export function updateEwaybillPrintIndex(shopId: number, shopPrintIndexInfos: ShopPrintIndexInfo[]): Promise<void> {
    return retry(() => {
        post(shopId, "/ewaybill/update_print_index", shopPrintIndexInfos, { hideModal: true });
    }, 5);
}

export enum PrintBatchNoLevel {
    COMPANY = "COMPANY",
    SHOP = "SHOP",
}

interface GetPrintBatchNoParams {
    level: PrintBatchNoLevel;
    shopId?: number;
}

export function getPrintBatchNo(params: GetPrintBatchNoParams) {
    return mainServerClient.post<{ batchNo: string }>("/ewaybill/get_print_batch_no", params);
}

export default ewaybillApi;
